'use client'

import { useEffect, useRef, useState } from 'react'
import { STAT_BAR_ITEMS } from '@/data/adaptive-learning'

const DURATION_MS = 1000

function formatStat(target: number, progress: number): string {
  const current = target * progress
  return Number.isInteger(target)
    ? `${Math.round(current)}`
    : current.toFixed(1)
}

/**
 * Count-up stat band. SSR renders the final values (no CLS, no zero flash for
 * no-JS); on first intersection the numbers animate from 0 over ~1s ease-out.
 * Skipped entirely under prefers-reduced-motion.
 */
export function AdaptiveStats() {
  const gridRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState<number>(1)

  useEffect(() => {
    const node = gridRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let frame = 0
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return
        observer.disconnect()
        const start = performance.now()
        const tick = (now: number): void => {
          const t = Math.min((now - start) / DURATION_MS, 1)
          setProgress(1 - Math.pow(1 - t, 3))
          if (t < 1) frame = requestAnimationFrame(tick)
        }
        setProgress(0)
        frame = requestAnimationFrame(tick)
      },
      { threshold: 0.35 }
    )
    observer.observe(node)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [])

  return (
    <section className="bg-surface border-border border-y py-12 md:py-16">
      <div
        ref={gridRef}
        className="mx-auto grid max-w-5xl grid-cols-2 gap-x-4 gap-y-9 px-5 text-center sm:grid-cols-4 md:px-6"
      >
        {STAT_BAR_ITEMS.map((stat, i) => (
          <div
            key={stat.label}
            className={i === 0 ? 'px-2' : 'border-border px-2 sm:border-l'}
          >
            <div className="font-heading text-ink text-4xl leading-none font-bold tracking-tight tabular-nums md:text-5xl">
              {formatStat(stat.value, progress)}
              {stat.suffix}
            </div>
            <p className="text-body mt-3 text-[13px] leading-snug">
              {stat.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}
